import { motion } from "framer-motion";
import { MEDIA } from "../lib/media";
import { Bed, Wifi, Shield, Users } from "lucide-react";

const ROOMS = [
  {
    image: MEDIA.room1,
    title: "La Palomar",
    price: "$75",
    desc: "Habitación para parejas con cama matrimonial, almohadas artesanales azules y decoración elegante.",
    capacity: "2 personas",
    includes: ["Desayuno incluido", "2 cantaritos de barro de bienvenida (con o sin licor)"],
  },
  {
    image: MEDIA.room3,
    title: "Habitación Doble",
    price: "$90",
    desc: "Cómoda habitación con 2 camas dobles, ideal para familias o amigos.",
    capacity: "2–3 personas",
    includes: ["Desayuno incluido"],
  },
  {
    image: MEDIA.room2,
    title: "La Clúster",
    price: "$120",
    desc: "Amplia habitación con camarote y cama adicional. Perfecta para grupos de amigos o familias.",
    capacity: "4 personas (máx. 6)",
    extra: "6 personas: $155 (+$35 por las 2 adicionales)",
    includes: ["Desayuno incluido"],
  },
];

const AMENITIES = [
  { icon: Bed, label: "Camas cómodas" },
  { icon: Wifi, label: "Wi-Fi gratis" },
  { icon: Shield, label: "Seguridad 24h" },
];

export const LodgingSection = () => {
  return (
    <section id="hospedaje" data-testid="lodging-section" className="py-24 md:py-32 bg-[#FAF7F2]">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <p className="overline text-[#A25F4B] mb-4">Descanso</p>
          <h2
            data-testid="lodging-title"
            className="font-heading text-4xl md:text-5xl tracking-tight text-[#2D2A26]"
          >
            Hospedaje
          </h2>
          <p className="mt-6 text-[#5C5750] text-base md:text-lg max-w-2xl mx-auto leading-relaxed font-body">
            Después de disfrutar nuestra cocina, quédate a descansar. Nuestras habitaciones
            ofrecen comodidad, privacidad y la tranquilidad que mereces.
          </p>

          {/* Amenities */}
          <div className="flex justify-center gap-8 mt-8">
            {AMENITIES.map((a, i) => (
              <div key={i} className="flex items-center gap-2 text-[#5C5750]">
                <a.icon size={18} className="text-[#3F4F34]" />
                <span className="font-body text-sm">{a.label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Room Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {ROOMS.map((room, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.15 }}
              data-testid={`lodging-room-${i}`}
              className="group"
            >
              <div className="gallery-item aspect-[4/3] mb-5">
                <img
                  src={room.image}
                  alt={room.title}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="font-heading text-2xl text-[#2D2A26] tracking-tight">
                {room.title}
              </h3>
              <p className="font-heading text-xl text-[#3F4F34] font-semibold mt-1">{room.price}<span className="text-[#5C5750] font-body text-xs font-normal"> / noche</span></p>
              <div className="flex items-center gap-1.5 mt-1.5">
                <Users size={14} className="text-[#A25F4B]" />
                <span className="font-body text-xs text-[#A25F4B] font-semibold">{room.capacity}</span>
              </div>
              {room.extra && (
                <p className="font-body text-xs text-[#5C5750] mt-1 italic">{room.extra}</p>
              )}
              <p className="mt-2 text-[#5C5750] font-body text-sm leading-relaxed">
                {room.desc}
              </p>
              <div className="mt-3 space-y-1">
                {room.includes.map((item, j) => (
                  <p key={j} className="font-body text-xs text-[#3F4F34] flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-[#3F4F34] inline-block" />
                    {item}
                  </p>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Pool Relaxation Video */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-16 aspect-video overflow-hidden"
          data-testid="lodging-pool-video"
        >
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
          >
            <source src={MEDIA.poolRelaxVideo} type="video/mp4" />
          </video>
        </motion.div>
        <p className="text-center mt-4 text-[#5C5750] font-body text-sm italic">
          Relájate junto a nuestra piscina y disfruta de bebidas refrescantes
        </p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-14"
        >
          <a
            href="#reservar"
            data-testid="lodging-cta-reservar"
            className="overline text-[0.7rem] bg-[#3F4F34] text-[#FAF7F2] px-10 py-3.5 hover:bg-[#2B3822] transition-all duration-300 hover:-translate-y-0.5 inline-block"
          >
            Solicitar Hospedaje
          </a>
        </motion.div>
      </div>
    </section>
  );
};
